'use client';

import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';

import { login } from '@/actions/login';
import { signInForm } from '@/config/contents';
import { loginSchema } from '@/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'next/dist/client/components/navigation';
import Link from 'next/link';
import { z } from 'zod';

import FormError from '@/components/app/auth/form-error';
import FormSuccess from '@/components/app/auth/form-success';
import { GoogleSignInButton } from '@/components/app/auth/signin-buttons';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

export default function LoginForm() {
    const [isPending, startTransition] = useTransition();
    const [showTwoFactor, setShowTwoFactor] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const searchParams = useSearchParams();
    const urlError =
        searchParams.get('error') === 'OAuthAccountNotLinked'
            ? 'Email already in use with different provider!'
            : '';
    const callbackUrl = searchParams.get('callbackUrl');

    // 1. Define your form.
    const form = useForm<z.infer<typeof loginSchema>>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const onSubmitEmail = async (values: z.infer<typeof loginSchema>) => {
        // Do something with the form values.
        // ✅ This will be type-safe and validated.
        setError('');
        setSuccess('');
        startTransition(() => {
            login(values, callbackUrl)
                .then((data) => {
                    console.log('---------data', data);

                    if (data?.error) {
                        form.reset();
                        setError(data.error);
                    }
                    if (data?.success) {
                        form.reset();
                        setSuccess(data.success);
                    }

                    if (data?.twoFactor) {
                        setShowTwoFactor(true);
                    }
                })
                .catch((error) => {
                    // Only log non-redirect errors
                    if (!error?.message?.includes('NEXT_REDIRECT')) {
                        console.log('---------error', error);
                        setError(
                            'An unexpected error occurred during login. Please try again.'
                        );
                    }
                    // Let Next.js handle redirects
                    return;
                });
        });
    };

    return (
        <div className="flex h-screen w-full items-center justify-center bg-linear-to-b from-blue-200 via-blue-100 to-white py-6 dark:from-blue-950 dark:via-blue-900 dark:to-gray-900">
            <div className="flex flex-col items-center justify-center gap-4  w-full">
                <div className="flex items-center gap-1 self-center font-medium text-center">
                    <span className="text-1xl font-bold font-heading">
                        heyBTW
                    </span>
                </div>
                <Card className="mx-auto p-2 w-[400px]">
                    <CardHeader className="text-center">
                        <CardTitle className="text-center text-2xl font-bold">
                            {signInForm.header}
                        </CardTitle>
                        <CardDescription className="text-center">
                            {signInForm.subheader}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="mx-auto space-y-6">
                            <div className="space-y-4">
                                <GoogleSignInButton disabled={isPending}>
                                    Login with Google
                                </GoogleSignInButton>
                                <div className="relative p-4">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-background px-2 text-muted-foreground">
                                            Or continue with
                                        </span>
                                    </div>
                                </div>
                                <Form {...form}>
                                    <form
                                        onSubmit={form.handleSubmit(
                                            onSubmitEmail
                                        )}
                                        className="space-y-0 flex flex-col gap-4"
                                    >
                                        {!showTwoFactor && (
                                            <div className="space-y-4">
                                                <div className="space-y-0">
                                                    <FormField
                                                        control={form.control}
                                                        name="email"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>
                                                                    Email
                                                                </FormLabel>
                                                                <FormControl>
                                                                    <Input
                                                                        disabled={
                                                                            isPending
                                                                        }
                                                                        placeholder=""
                                                                        type="email"
                                                                        autoComplete="email"
                                                                        {...field}
                                                                    />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                                <div className="space-y-0 pt-2">
                                                    <FormField
                                                        control={form.control}
                                                        name="password"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <div className="flex items-center justify-between">
                                                                    <FormLabel>
                                                                        Password
                                                                    </FormLabel>
                                                                    <Link
                                                                        href="/auth/reset"
                                                                        className="text-sm font-medium underline dark:text-muted-foreground"
                                                                        prefetch={
                                                                            false
                                                                        }
                                                                    >
                                                                        Forgot
                                                                        password?
                                                                    </Link>
                                                                </div>
                                                                <FormControl>
                                                                    <Input
                                                                        disabled={
                                                                            isPending
                                                                        }
                                                                        placeholder=""
                                                                        type="password"
                                                                        autoComplete="current-password"
                                                                        {...field}
                                                                    />
                                                                </FormControl>
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {showTwoFactor && (
                                            <div className="space-y-2 p-0">
                                                <FormField
                                                    control={form.control}
                                                    name="code"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <div className="flex items-center justify-between">
                                                                <FormLabel>
                                                                    Two Factor
                                                                    Code
                                                                </FormLabel>
                                                            </div>
                                                            <FormControl>
                                                                <Input
                                                                    disabled={
                                                                        isPending
                                                                    }
                                                                    placeholder=""
                                                                    type="text"
                                                                    autoComplete="one-time-code"
                                                                    {...field}
                                                                />
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>
                                        )}
                                        <FormError
                                            message={error || urlError}
                                        />
                                        <FormSuccess message={success} />
                                        <Button
                                            type="submit"
                                            disabled={isPending}
                                            className="w-full dark:bg-primary dark:text-primary-foreground"
                                        >
                                            {showTwoFactor
                                                ? 'Confirm'
                                                : 'Login'}
                                        </Button>
                                    </form>
                                </Form>
                            </div>

                            <div className="text-center text-sm text-muted-foreground dark:text-muted-foreground">
                                Don&apos;t have an account yet?{' '}
                                <Link
                                    href="/auth/register"
                                    className="underline dark:text-muted-foreground"
                                    prefetch={false}
                                >
                                    Sign up
                                </Link>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <div className="text-muted-foreground hover:[a]:*:text-primary text-center text-xs text-balance [a]:*:underline [a]:*:underline-offset-4">
                    <div className="flex items-center justify-center gap-1">
                        By clicking login, you agree to our{' '}
                    </div>
                    <div className="flex items-center justify-center gap-1">
                        <a href="/terms" className="underline">
                            Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="/privacy" className="underline">
                            Privacy Policy
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
